/**
 * MEMBER AVATAR (Phase 6)
 * =======================
 *
 * One place that decides what a member's picture is, so every surface shows the same thing.
 *
 * Authoritative fallback order:
 *   1. the member's own uploaded photograph  (/api/profile/photo/{id}, authenticated)
 *   2. their Google account photograph        (never copied into our storage)
 *   3. their initials, from their current display name
 *   4. the Arvexis symbol, only when there is no usable name
 *
 * Each image candidate falls through on a load error, so a photo that cannot be fetched shows
 * the next real thing rather than an empty circle.
 */

import React, { useMemo, useState } from 'react';
import { ArvexisSymbol } from './ArvexisSymbol';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const memberInitials = (name) => {
  const parts = String(name || '')
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const MemberAvatar = ({ user, size = 120, symbolSize = null,
                               initialsStyle = null, className = '' }) => {
  const [failed, setFailed] = useState([]);

  const candidates = useMemo(() => {
    const list = [];
    const photoId = user?.profile_photo?.photo_id;
    if (photoId) {
      // No cache-buster: every saved photo mints a new id, so the reference is already unique.
      // A per-surface buster made the same photo look like a different one on each surface.
      list.push({
        key: `uploaded:${photoId}`,
        src: `${API}/profile/photo/${photoId}`,
        testId: 'member-avatar-uploaded',
      });
    }
    if (user?.picture) {
      list.push({ key: `google:${user.picture}`, src: user.picture, testId: 'member-avatar-google' });
    }
    return list;
  }, [user?.profile_photo?.photo_id, user?.picture]);

  const active = candidates.find((c) => !failed.includes(c.key));
  const initials = memberInitials(user?.name);

  const shell = {
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (active) {
    return (
      <div style={shell} className={className} data-testid="member-avatar">
        <img
          key={active.key}
          src={active.src}
          alt=""
          referrerPolicy="no-referrer"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          data-testid={active.testId}
          onError={() => setFailed((prev) => [...prev, active.key])}
        />
      </div>
    );
  }

  if (initials) {    return (
      <div style={shell} className={className} data-testid="member-avatar">
        <span
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: Math.round(size * 0.4),
            fontWeight: 400,
            letterSpacing: '0.04em',
            color: 'rgba(244, 239, 231, 0.92)',
            lineHeight: 1,
            userSelect: 'none',
            // A surface that already had its own accepted type treatment for initials keeps it.
            // The precedence is shared; the typography of an accepted surface is not overridden.
            ...(initialsStyle || {}),
          }}
          data-testid="member-avatar-initials"
        >
          {initials}
        </span>
      </div>
    );
  }

  return (
    <div style={shell} className={className} data-testid="member-avatar">
      <ArvexisSymbol size={symbolSize || Math.round(size * 0.54)} />
    </div>
  );
};

export default MemberAvatar;
